/**
 * Authenticated fetch wrapper with automatic token refresh.
 *
 * Flow:
 *  1. Attach current access token to request
 *  2. If response is 401 → attempt token refresh
 *  3. On successful refresh → retry original request once with new token
 *  4. If refresh fails → fire SESSION_EXPIRED_EVENT so the app can redirect to /login
 */

/// <reference lib="dom" />

import { getAccessToken, getRefreshToken, isSessionExpired } from './auth-token'
import { refreshSessionDirect } from './auth-direct'
import { UnauthorizedError } from './errors'

export const SESSION_EXPIRED_EVENT = 'session:expired'

function emitSessionExpired() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT))
  }
}

// Prevent concurrent refresh calls — reuse the in-flight promise
let refreshPromise: Promise<string> | null = null

async function getValidAccessToken(): Promise<string> {
  // Proactively refresh if token is close to expiry
  if (isSessionExpired()) {
    const refreshToken = getRefreshToken()
    if (!refreshToken) {
      emitSessionExpired()
      throw new UnauthorizedError('Session expired')
    }

    if (!refreshPromise) {
      refreshPromise = refreshSessionDirect(refreshToken)
        .then((s) => s.access_token)
        .finally(() => { refreshPromise = null })
    }

    return refreshPromise
  }

  const token = getAccessToken()
  if (!token) {
    emitSessionExpired()
    throw new UnauthorizedError('Not authenticated')
  }
  return token
}

export type AuthHeaders = {
  apikey: string
  Authorization: string
}

const SUPABASE_ANON_KEY = (import.meta as any).env?.VITE_SUPABASE_ANON_KEY as string

/**
 * Returns auth headers with a valid (possibly refreshed) access token.
 * Use this in place of manually calling getAccessToken() in API files.
 */
export async function getAuthHeaders(): Promise<AuthHeaders> {
  const token = await getValidAccessToken()
  return {
    apikey: SUPABASE_ANON_KEY,
    Authorization: `Bearer ${token}`,
  }
}

/**
 * Drop-in replacement for fetch() that handles 401 → refresh → retry.
 * Pass the same args as fetch(). Auth headers are injected automatically.
 */
export async function fetchWithAuth(
  url: string,
  options: RequestInit = {}
): Promise<Response> {
  const token = await getValidAccessToken()

  const makeRequest = (accessToken: string) =>
    fetch(url, {
      ...options,
      headers: {
        ...options.headers,
        apikey: SUPABASE_ANON_KEY,
        Authorization: `Bearer ${accessToken}`,
      },
    })

  const response = await makeRequest(token)

  // If 401, try refreshing once
  if (response.status === 401) {
    const refreshToken = getRefreshToken()
    if (!refreshToken) {
      emitSessionExpired()
      throw new UnauthorizedError('Session expired')
    }

    try {
      if (!refreshPromise) {
        refreshPromise = refreshSessionDirect(refreshToken)
          .then((s) => s.access_token)
          .finally(() => { refreshPromise = null })
      }
      const newToken = await refreshPromise
      return makeRequest(newToken)
    } catch {
      emitSessionExpired()
      throw new UnauthorizedError('Session expired')
    }
  }

  return response
}
